import { useMemo, createContext, useContext } from 'react';
import { useDeviceLayout, DeviceLayout } from '../../../hooks/useDeviceLayout';

/**
 * 플랫폼별 레이아웃 컴포넌트 타입
 */
interface AdaptiveLayoutProps<P extends object> {
  /** PC 레이아웃 (비율 < 1.0) */
  desktop: React.ComponentType<P>;
  /** 폴드폰/태블릿 레이아웃 (비율 1.0 ~ 1.5) */
  fold: React.ComponentType<P>;
  /** 일반폰 레이아웃 (비율 > 1.5) */
  phone: React.ComponentType<P>;
  /** 레이아웃 컴포넌트에 전달할 props */
  props: P;
}

/**
 * 비율 기반 적응형 레이아웃 선택기
 *
 * 화면 비율(height/width)에 따라 적절한 레이아웃 컴포넌트를 렌더링합니다.
 *
 * @example
 * ```tsx
 * <AdaptiveLayout
 *   desktop={InspectionDetailDesktop}
 *   fold={InspectionDetailFold}
 *   phone={InspectionDetailPhone}
 *   props={{ inspection }}
 * />
 * ```
 */
export function AdaptiveLayout<P extends object>({
  desktop: Desktop,
  fold: Fold,
  phone: Phone,
  props,
}: AdaptiveLayoutProps<P>) {
  const layout = useDeviceLayout();

  // 레이아웃 컴포넌트 선택
  const LayoutComponent = useMemo(() => {
    switch (layout) {
      case 'desktop':
        return Desktop;
      case 'fold':
        return Fold;
      case 'phone':
        return Phone;
      default:
        return Desktop;
    }
  }, [layout, Desktop, Fold, Phone]);

  return <LayoutComponent {...props} />;
}

/**
 * 현재 레이아웃 정보를 자식에게 전달하는 컨텍스트 버전
 */
interface AdaptiveLayoutWithContextProps<P extends object> extends AdaptiveLayoutProps<P> {
  /** 레이아웃 정보를 자식에게 전달할지 여부 */
  _provideContext?: boolean;
}

// 레이아웃 컨텍스트
export const LayoutContext = createContext<DeviceLayout>('desktop');

export function AdaptiveLayoutWithContext<P extends object>({
  desktop: Desktop,
  fold: Fold,
  phone: Phone,
  props,
}: AdaptiveLayoutWithContextProps<P>) {
  const layout = useDeviceLayout();

  const LayoutComponent = useMemo(() => {
    switch (layout) {
      case 'desktop':
        return Desktop;
      case 'fold':
        return Fold;
      case 'phone':
        return Phone;
      default:
        return Desktop;
    }
  }, [layout, Desktop, Fold, Phone]);

  return (
    <LayoutContext.Provider value={layout}>
      <LayoutComponent {...props} />
    </LayoutContext.Provider>
  );
}

/**
 * 현재 레이아웃을 가져오는 훅 (컨텍스트 사용)
 */
export function useLayoutContext(): DeviceLayout {
  return useContext(LayoutContext);
}

export default AdaptiveLayout;
